import React, { useState, useRef, useCallback, useMemo } from 'react';
import { Trip, ItineraryItem } from '../../types/trip';
import { TravelEntry } from '../../types/travel';
import { getDisplayMembers } from '../../utils/members';
import { useDesign } from '../../designs';
import { SlidersHorizontal, Map, List, Users, KeyRound, Check, ChevronDown, ChevronUp } from 'lucide-react';
import AddItemModal from './AddItemModal';
import ItemDetailView from './ItemDetailView';
import EditLodgingModal from './EditLodgingModal';
import MapView from './MapView';
import OrnamentalDivider from '../../components/ui/OrnamentalDivider';
import DesignButton from '../../components/ui/DesignButton';
import { formatTime12h } from '../../components/ui/TimeSpinner';
import { getTripDays, formatDayHeader, formatDayTab } from '../../utils/dates';
import { parseStreetAddress, parseVenueName as parseVenueFromAddress } from '../../utils/address';
import SinglePinMap from '../../components/ui/SinglePinMap';

interface ItineraryTabProps {
  trip: Trip;
  tripId: string;
  isPlanner: boolean;
  canEdit: boolean;
  currentUid: string;
  claimedMemberUid: string | null;
  onDetailView?: (isDetail: boolean) => void;
}

type FilterType = 'all' | 'dining' | 'activity';
type StatusFilter = 'all' | 'confirmed' | 'considering';
type ShowFilter = 'all' | 'plans';

/** A unified timeline entry — either an itinerary item or a travel stage direction */
type TimelineEntry =
  | { kind: 'item'; data: ItineraryItem; shifted?: boolean }
  | { kind: 'travel'; data: TravelEntry; firstName: string };


/* ─── Lodging Key Card (collapsible) ─── */
function LodgingKeyCard({ trip, onDetail, onAddLodging, isPlanner }: {
  trip: Trip; onDetail: () => void; onAddLodging: () => void; isPlanner: boolean;
}) {
  const design = useDesign();
  const d = design.tokens;
  const fs = design.typography.fontSize;
  const displayFont = `'${design.typography.displayFont}', serif`;
  const lodging = trip.lodging;
  const memberCount = getDisplayMembers(trip.members || {}).length;
  const [expanded, setExpanded] = useState(false);

  if (!lodging?.name) {
    if (!isPlanner) return null;
    return (
      <div
        onClick={onAddLodging}
        style={{
          margin: '0 26px 16px', padding: '12px 16px',
          border: '1px dashed rgba(200,146,58,0.12)', borderRadius: '2px',
          textAlign: 'center', cursor: 'pointer',
        }}
      >
        <span style={{ fontFamily: "'Raleway', sans-serif", fontSize: fs.body, fontWeight: 400, color: d.accent }}>
          Add lodging
        </span>
      </div>
    );
  }

  if (!expanded) {
    // Collapsed — name left, code right, chevron far right
    return (
      <div
        onClick={() => setExpanded(true)}
        style={{
          margin: '0 26px 16px', padding: '10px 14px',
          backgroundColor: d.bgSurface,
          border: `1px solid ${d.border}`,
          borderRadius: '2px',
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: '12px',
        }}
      >
        {/* Hotel name — left justified, truncates */}
        <span style={{
          fontFamily: "'Raleway', sans-serif", fontSize: fs.bodyDetail, fontWeight: 500,
          color: d.textPrimary, flex: 1, minWidth: 0,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {lodging.name}
        </span>
        {/* Code — right justified */}
        {lodging.accessCode && (
          <span style={{
            fontFamily: "'Raleway', sans-serif", fontSize: fs.bodyDetail, fontWeight: 300,
            color: d.textSecondary, flexShrink: 0, whiteSpace: 'nowrap',
          }}>
            Code:{' '}
            <span style={{ color: d.accent, fontWeight: 700, letterSpacing: '1px' }}>{lodging.accessCode}</span>
          </span>
        )}
        <ChevronDown size={16} strokeWidth={1.5} color={d.textSecondary} style={{ flexShrink: 0 }} />
      </div>
    );
  }

  // Expanded — full three-column layout, tap chevron or card to collapse
  return (
    <div
      style={{
        margin: '0 26px 16px', padding: '12px 16px',
        backgroundColor: d.bgSurface,
        border: `1px solid ${d.border}`, borderRadius: '2px',
      }}
    >
      {/* Three-column layout: code | name+address | guests */}
      <div
        onClick={onDetail}
        style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
      >
        {/* Left — access code */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', minWidth: '40px' }}>
          <span style={{
            fontFamily: displayFont, fontSize: fs.bodyInput, fontWeight: 700,
            color: lodging.accessCode ? d.accent : d.textTertiary,
            letterSpacing: '1px',
          }}>
            {lodging.accessCode || '—'}
          </span>
          <KeyRound size={13} strokeWidth={1.5} color={d.textSecondary} />
        </div>

        {/* Vertical divider */}
        <div style={{ width: '1px', alignSelf: 'stretch', backgroundColor: 'rgba(200,146,58,0.08)', margin: '0 14px' }} />

        {/* Center — name + address */}
        <div style={{ flex: 1, textAlign: 'center', minWidth: 0 }}>
          <div style={{
            fontFamily: displayFont, fontSize: fs.bodyLarge, fontWeight: 700,
            color: d.textPrimary, marginBottom: '3px',
          }}>{lodging.name}</div>
          {lodging.address && (
            lodging.url ? (
              <a href={lodging.url} target="_blank" rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{ fontFamily: "'Raleway', sans-serif", fontSize: fs.bodyDetail, fontWeight: 300, color: d.textSecondary, textDecoration: 'none' }}>
                {parseStreetAddress(lodging.address)}
              </a>
            ) : (
              <span style={{ fontFamily: "'Raleway', sans-serif", fontSize: fs.bodyDetail, fontWeight: 300, color: d.textSecondary }}>
                {parseStreetAddress(lodging.address)}
              </span>
            )
          )}
        </div>

        {/* Vertical divider */}
        <div style={{ width: '1px', alignSelf: 'stretch', backgroundColor: 'rgba(200,146,58,0.08)', margin: '0 14px' }} />

        {/* Right — guest count */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', minWidth: '40px' }}>
          <span style={{ fontFamily: displayFont, fontSize: fs.bodyInput, fontWeight: 700, color: d.accent }}>
            {memberCount}
          </span>
          <Users size={13} strokeWidth={1.5} color={d.textSecondary} />
        </div>
      </div>

      {/* Collapse indicator */}
      <button
        onClick={(e) => { e.stopPropagation(); setExpanded(false); }}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: '100%', marginTop: '8px', padding: '4px',
          background: 'none', border: 'none', cursor: 'pointer',
        }}
      >
        <ChevronUp size={14} strokeWidth={1.5} color={d.textSecondary} />
      </button>
    </div>
  );
}

/* ─── Activity Card ─── */
function ActivityCard({ item, onClick, shiftedDayLabel }: { item: ItineraryItem; onClick: () => void; shiftedDayLabel?: string }) {
  const design = useDesign();
  const d = design.tokens;
  const fs = design.typography.fontSize;
  const displayFont = `'${design.typography.displayFont}', serif`;
  const isConfirmed = item.status === 'confirmed';

  const timeColor = isConfirmed ? d.accent : d.textTertiary;
  const nameColor = isConfirmed ? d.textPrimary : 'rgba(237,228,216,0.80)';
  const line2Color = isConfirmed ? d.accent : d.textSecondary;

  const timeParts = item.time ? formatTime12h(item.time).split(' ') : null;
  // Line 2: dining shows street only. Activities show "Venue, Street" if the
  // address has a non-numeric prefix segment (e.g., "House of Blues, 225 Decatur St").
  const street = parseStreetAddress(item.address || '');
  const line2Text = (() => {
    if (item.type === 'activity') {
      // Prefer persisted venue; fall back to parsing address for legacy items.
      const venue = item.venue || parseVenueFromAddress(item.address || '');
      if (venue && street && venue !== street) return `${venue}, ${street}`;
      return venue || street;
    }
    return street;
  })();

  return (
    <div
      onClick={onClick}
      style={{
        display: 'grid', gridTemplateColumns: '46px 1fr',
        gap: '12px', padding: '20px 0 22px', alignItems: 'start',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        cursor: 'pointer',
        opacity: isConfirmed ? 1 : 0.85,
      }}
    >
      {/* Time column */}
      <div style={{ textAlign: 'left', paddingTop: '2px' }}>
        {timeParts ? (
          <>
            <div style={{ fontFamily: displayFont, fontSize: fs.bodyLarge, fontWeight: 700, lineHeight: 1.2, color: timeColor }}>
              {timeParts[0]}
            </div>
            <div style={{ fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, fontWeight: 300, color: timeColor, marginTop: '1px' }}>
              {timeParts[1]}{shiftedDayLabel ? ` (${shiftedDayLabel})` : ''}
            </div>
          </>
        ) : (
          <div style={{ fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, fontWeight: 300, color: d.textTertiary }}>—</div>
        )}
      </div>

      {/* Content */}
      <div>
        {/* Line 1: name + status */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '2px' }}>
          <span style={{ fontFamily: "'Raleway', sans-serif", fontSize: fs.itemName, fontWeight: 500, color: nameColor, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
            {item.name}
          </span>
          {isConfirmed ? (
            <Check size={14} strokeWidth={2} color={d.confirmed} style={{ flexShrink: 0, marginLeft: '8px' }} />
          ) : (
            <span style={{
              fontFamily: "'Raleway', sans-serif", fontSize: fs.detail,
              fontWeight: 300, fontStyle: 'italic',
              color: d.textTertiary, flexShrink: 0, marginLeft: '8px',
            }}>
              Considering
            </span>
          )}
        </div>

        {/* Line 2: Bodoni italic location — single line, ellipsis if too long */}
        {line2Text && (
          <div style={{
            fontFamily: displayFont, fontSize: fs.bodyInput, fontWeight: 400,
            fontStyle: 'italic', color: line2Color, lineHeight: 1.3, marginBottom: '2px',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>{line2Text}</div>
        )}

        {/* Line 3: notes */}
        {item.notes && (
          <div style={{
            fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, fontWeight: 300,
            color: d.textSecondary, lineHeight: 1.4, marginTop: '1px',
          }}>{item.notes}</div>
        )}
      </div>
    </div>
  );
}

/* ─── Inline Travel Line (stage direction between itinerary items) ─── */
function TravelLine({ entry, firstName }: { entry: TravelEntry; firstName: string }) {
  const design = useDesign();
  const d = design.tokens;
  const fs = design.typography.fontSize;
  const time = entry.arriveAtLodging ? formatTime12h(entry.arriveAtLodging) : '';
  const verb = entry.direction === 'arrival' ? 'arrives on site' : 'leaves site';
  const airport = entry.airport || '';
  const parts = [time, `${firstName} ${verb}`, airport].filter(Boolean);
  const text = parts.join(' · ');

  return (
    <div style={{
      padding: '10px 0 10px 58px',
      borderBottom: '1px solid rgba(255,255,255,0.08)',
    }}>
      <span style={{
        fontFamily: "'Raleway', sans-serif", fontSize: fs.bodyDetail, fontWeight: 300,
        color: d.textSecondary,
      }}>
        {text}
      </span>
    </div>
  );
}

/* ─── Main ItineraryTab ─── */
export default function ItineraryTab({ trip, tripId, isPlanner, canEdit, currentUid, claimedMemberUid, onDetailView }: ItineraryTabProps) {
  const design = useDesign();
  const d = design.tokens;
  const fs = design.typography.fontSize;
  const displayFont = `'${design.typography.displayFont}', serif`;

  const [filter, setFilter] = useState<FilterType>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [showFilter, setShowFilter] = useState<ShowFilter>('all');
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [selectedDay, setSelectedDay] = useState<string>('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ItineraryItem | null>(null);
  const [editItem, setEditItem] = useState<ItineraryItem | null>(null);
  const [showLodgingModal, setShowLodgingModal] = useState(false);
  const [showLodgingDetail, setShowLodgingDetail] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const touchRef = useRef<{ startX: number; startY: number; isDragging: boolean }>({ startX: 0, startY: 0, isDragging: false });

  const meta = trip.meta;
  const members = trip.members || {};
  const memberCount = Object.keys(members).length;
  const items = meta && trip.itinerary ? Object.values(trip.itinerary) : [];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tripDays = useMemo(() => meta ? getTripDays(meta.startDate, meta.endDate) : [], [meta?.startDate, meta?.endDate]);

  const liveSelectedItem = selectedItem && trip.itinerary ? trip.itinerary[selectedItem.itemId] || null : null;
  const isInDetailView = !!(selectedItem && liveSelectedItem) || !!editItem || showLodgingDetail || showAddModal || showLodgingModal;
  React.useEffect(() => { onDetailView?.(isInDetailView); }, [isInDetailView, onDetailView]);

  const dayOptions = useMemo(() => ['all', ...tripDays], [tripDays]);
  const navigateDay = useCallback((direction: 'left' | 'right') => {
    const idx = dayOptions.indexOf(selectedDay);
    const nextIdx = direction === 'left' ? idx + 1 : idx - 1;
    if (nextIdx >= 0 && nextIdx < dayOptions.length) {
      setIsAnimating(true);
      setSwipeOffset(direction === 'left' ? -100 : 100);
      setTimeout(() => {
        setSelectedDay(dayOptions[nextIdx]);
        setSwipeOffset(direction === 'left' ? 100 : -100);
        requestAnimationFrame(() => { setSwipeOffset(0); setIsAnimating(false); });
      }, 150);
    }
  }, [selectedDay, dayOptions]);
  const handleTouchStart = useCallback((e: React.TouchEvent) => { touchRef.current = { startX: e.touches[0].clientX, startY: e.touches[0].clientY, isDragging: false }; }, []);
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const dx = e.touches[0].clientX - touchRef.current.startX;
    const dy = e.touches[0].clientY - touchRef.current.startY;
    if (!touchRef.current.isDragging && Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 10) touchRef.current.isDragging = true;
    if (touchRef.current.isDragging) setSwipeOffset(dx * 0.4);
  }, []);
  const handleTouchEnd = useCallback(() => {
    if (touchRef.current.isDragging) {
      if (swipeOffset < -40) navigateDay('left');
      else if (swipeOffset > 40) navigateDay('right');
      else setSwipeOffset(0);
    }
    touchRef.current.isDragging = false;
  }, [swipeOffset, navigateDay]);

  if (!meta) return null;

  // Late-night activities (12:00 AM – 3:59 AM) belong to the PREVIOUS day's
  // section — a 1 AM bar visit is Thursday night, not Friday morning. This
  // helper returns the section day and a "shifted" flag; the actual stored
  // item.day/item.time are untouched.
  const shiftedDayFor = (item: ItineraryItem): { sectionDay: string | undefined; shifted: boolean } => {
    if (!item.day) return { sectionDay: undefined, shifted: false };
    if (!item.time) return { sectionDay: item.day, shifted: false };
    const hour = parseInt(item.time.split(':')[0] || '0', 10);
    if (hour >= 0 && hour < 4) {
      const prev = new Date(item.day + 'T00:00:00');
      prev.setDate(prev.getDate() - 1);
      const y = prev.getFullYear();
      const m = (prev.getMonth() + 1).toString().padStart(2, '0');
      const d = prev.getDate().toString().padStart(2, '0');
      return { sectionDay: `${y}-${m}-${d}`, shifted: true };
    }
    return { sectionDay: item.day, shifted: false };
  };

  const filtered = items.filter((item) => {
    if (filter !== 'all' && item.type !== filter) return false;
    if (statusFilter !== 'all' && item.status !== statusFilter) return false;
    if (selectedDay !== 'all') {
      const { sectionDay } = shiftedDayFor(item);
      if (sectionDay !== selectedDay) return false;
    }
    return true;
  });

  // Build travel entries for weaving into the timeline
  const travelEntries: TravelEntry[] = trip.travel ? Object.values(trip.travel) : [];

  // For sort purposes, treat 00:00-03:59 items as 24:00-27:59 so they appear
  // after the rest of the previous day's evening entries.
  const sortKey = (time: string, shifted: boolean): string => {
    if (!time) return '';
    if (!shifted) return time;
    const [h, m] = time.split(':');
    const hh = (parseInt(h, 10) + 24).toString();
    return `${hh}:${m}`;
  };
  const sortTimeline = (a: TimelineEntry, b: TimelineEntry) => {
    const aShifted = a.kind === 'item' ? !!a.shifted : false;
    const bShifted = b.kind === 'item' ? !!b.shifted : false;
    const aTime = a.kind === 'item' ? sortKey(a.data.time || '', aShifted) : (a.data.arriveAtLodging || '');
    const bTime = b.kind === 'item' ? sortKey(b.data.time || '', bShifted) : (b.data.arriveAtLodging || '');
    // Items without times go to bottom
    if (!aTime && bTime) return 1;
    if (aTime && !bTime) return -1;
    if (aTime !== bTime) return aTime.localeCompare(bTime);
    // Travel entries sort after items at the same time
    if (a.kind !== b.kind) return a.kind === 'item' ? -1 : 1;
    return 0;
  };

  const daysToShow = selectedDay === 'all' ? tripDays : [selectedDay];
  const timeline: Record<string, TimelineEntry[]> = {};
  const unscheduledTimeline: TimelineEntry[] = [];
  daysToShow.forEach((day) => { timeline[day] = []; });

  // Add itinerary items — route late-night items into the previous day's section.
  filtered.forEach((item) => {
    if (!item.day) {
      if (selectedDay === 'all') unscheduledTimeline.push({ kind: 'item', data: item });
      return;
    }
    const { sectionDay, shifted } = shiftedDayFor(item);
    if (sectionDay && timeline[sectionDay]) {
      timeline[sectionDay].push({ kind: 'item', data: item, shifted });
    }
  });

  // Weave in travel entries (if showFilter is 'all')
  if (showFilter === 'all') {
    travelEntries.forEach((entry) => {
      if (!entry.day || !timeline[entry.day]) return;
      if (selectedDay !== 'all' && entry.day !== selectedDay) return;
      const member = members[entry.memberUid];
      const firstName = member?.displayName?.split(' ')[0] || 'Guest';
      timeline[entry.day].push({ kind: 'travel', data: entry, firstName });
    });
  }

  Object.values(timeline).forEach((arr) => arr.sort(sortTimeline));
  unscheduledTimeline.sort(sortTimeline);

  // Detail views
  if (selectedItem && liveSelectedItem) {
    return <ItemDetailView item={liveSelectedItem} members={members} tripId={tripId} isPlanner={isPlanner} canEdit={canEdit} currentUid={currentUid} claimedMemberUid={claimedMemberUid} onBack={() => setSelectedItem(null)} onEdit={() => { setEditItem(liveSelectedItem); setSelectedItem(null); }} />;
  }
  if (showLodgingDetail && trip.lodging) {
    const lodging = trip.lodging;
    const hasLodgingMap = (lodging.lat != null && lodging.lng != null) || !!lodging.address;
    const formatDate = (iso: string) => new Date(iso + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    return (
      <div style={{ padding: '16px 26px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <button onClick={() => setShowLodgingDetail(false)} style={{ background: 'none', border: 'none', fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, fontWeight: 300, color: d.textSecondary, cursor: 'pointer' }}>← Back</button>
          {isPlanner && <button onClick={() => { setShowLodgingDetail(false); setShowLodgingModal(true); }} style={{ background: 'none', border: 'none', fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, fontWeight: 300, color: d.textSecondary, cursor: 'pointer' }}>Edit ✎</button>}
        </div>
        <span style={{ fontFamily: "'Raleway', sans-serif", fontSize: fs.sectionHeader, fontWeight: 700, letterSpacing: '3px', textTransform: 'uppercase', padding: '2px 8px', backgroundColor: d.textTertiary, borderRadius: '2px', color: d.textPrimary }}>Lodging</span>
        {lodging.url ? (
          <a href={lodging.url} target="_blank" rel="noopener noreferrer" style={{ display: 'block', fontFamily: displayFont, fontSize: '24px', fontWeight: 700, color: d.accent, margin: '10px 0 4px', textDecoration: 'none' }}>{lodging.name} →</a>
        ) : (
          <h2 style={{ fontFamily: displayFont, fontSize: '24px', fontWeight: 700, color: d.textPrimary, margin: '10px 0 4px' }}>{lodging.name}</h2>
        )}
        {lodging.address && <p style={{ fontFamily: "'Raleway', sans-serif", fontSize: fs.bodyDetail, fontWeight: 300, color: d.textSecondary, margin: '0 0 12px' }}>{parseStreetAddress(lodging.address)}</p>}
        {lodging.checkInDate && (
          <p style={{ fontFamily: "'Raleway', sans-serif", fontSize: fs.bodyDetail, fontWeight: 300, color: d.textSecondary, margin: '0 0 4px' }}>
            Check-in: {formatDate(lodging.checkInDate)}{lodging.checkInTime && ` at ${formatTime12h(lodging.checkInTime)}`}
          </p>
        )}
        {lodging.checkOutDate && (
          <p style={{ fontFamily: "'Raleway', sans-serif", fontSize: fs.bodyDetail, fontWeight: 300, color: d.textSecondary, margin: '0 0 12px' }}>
            Check-out: {formatDate(lodging.checkOutDate)}{lodging.checkOutTime && ` at ${formatTime12h(lodging.checkOutTime)}`}
          </p>
        )}
        {hasLodgingMap && (
          <div style={{ marginBottom: '16px' }}>
            <SinglePinMap lat={lodging.lat} lng={lodging.lng} address={lodging.address} height={180} />
          </div>
        )}
        {lodging.accessCode && (
          <div style={{ padding: '16px', border: `1px solid ${d.border}`, borderRadius: '2px', backgroundColor: d.bgSurface, marginBottom: '16px' }}>
            <span style={{ fontFamily: "'Raleway', sans-serif", fontSize: fs.sectionHeader, fontWeight: 700, letterSpacing: '3px', textTransform: 'uppercase', color: d.textSecondary }}>Access Code</span>
            <p style={{ fontFamily: "'Raleway', sans-serif", fontSize: fs.displaySmall, fontWeight: 400, color: d.textPrimary, margin: '4px 0 0' }}>{lodging.accessCode}</p>
          </div>
        )}
        {lodging.notes && <p style={{ fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, fontWeight: 300, color: d.textSecondary, margin: '0 0 24px' }}>{lodging.notes}</p>}
        <div style={{ height: '1px', backgroundColor: d.border, marginBottom: '16px' }} />
        <p style={{ fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, fontWeight: 300, color: d.textSecondary, margin: 0 }}>{memberCount} guest{memberCount !== 1 ? 's' : ''}</p>
      </div>
    );
  }

  const hasActiveFilters = filter !== 'all' || statusFilter !== 'all' || showFilter !== 'all';

  return (
    <>
      {/* ═══ LODGING KEY CARD ═══ */}
      <LodgingKeyCard trip={trip} onDetail={() => setShowLodgingDetail(true)} onAddLodging={() => setShowLodgingModal(true)} isPlanner={isPlanner} />

      {/* ═══ DAY TABS ═══ */}
      <div style={{
        display: 'flex', alignItems: 'center',
        padding: '0 26px', minHeight: '44px',
      }}>
        <div style={{ display: 'flex', gap: '4px', flex: 1, overflowX: 'auto' }}>
          {['all', ...tripDays].map((day) => {
            const isActive = selectedDay === day;
            const label = day === 'all' ? 'ALL' : formatDayTab(day);
            return (
              <button key={day} onClick={() => setSelectedDay(day)} style={{
                fontFamily: "'Raleway', sans-serif", fontSize: fs.bodyInput,
                fontWeight: isActive ? 600 : 400,
                padding: '10px 8px', backgroundColor: 'transparent',
                color: isActive ? d.textPrimary : d.textSecondary,
                border: 'none',
                borderBottom: isActive ? `2px solid ${d.accent}` : '2px solid transparent',
                cursor: 'pointer', whiteSpace: 'nowrap',
              }}>{label}</button>
            );
          })}
        </div>
        <button onClick={() => setShowFilterMenu(!showFilterMenu)} style={{
          background: 'none', border: 'none', cursor: 'pointer',
          padding: '10px 6px', flexShrink: 0, display: 'flex', alignItems: 'center',
        }}>
          <SlidersHorizontal size={16} strokeWidth={1.5} color={hasActiveFilters ? d.accent : d.textSecondary} />
        </button>
        <button onClick={() => setShowMap(!showMap)} style={{
          background: 'none', border: 'none', cursor: 'pointer',
          padding: '10px 6px', flexShrink: 0, display: 'flex', alignItems: 'center',
        }}>
          {showMap ? <List size={16} strokeWidth={1.5} color={d.accent} /> : <Map size={16} strokeWidth={1.5} color={d.textSecondary} />}
        </button>
      </div>

      {/* Spacer (active tab has its own underline; no full-width rule) */}
      <div style={{ height: '16px' }} />

      {/* Filter rows */}
      {showFilterMenu && (
        <div style={{ padding: '0 26px 12px' }}>
          <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
            {(['all', 'confirmed', 'considering'] as StatusFilter[]).map((s) => (
              <button key={s} onClick={() => setStatusFilter(s)} style={{
                fontFamily: "'Raleway', sans-serif", fontSize: fs.metaLabel, fontWeight: 600,
                letterSpacing: '1.5px', textTransform: 'uppercase',
                padding: '4px 10px', borderRadius: '999px',
                border: `1px solid ${statusFilter === s ? d.accent : d.border}`,
                backgroundColor: statusFilter === s ? d.bgSurface : 'transparent',
                color: statusFilter === s ? d.accent : d.textTertiary, cursor: 'pointer',
              }}>{s === 'all' ? 'All Status' : s}</button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
            {(['all', 'dining', 'activity'] as FilterType[]).map((f) => (
              <button key={f} onClick={() => setFilter(f)} style={{
                fontFamily: "'Raleway', sans-serif", fontSize: fs.metaLabel, fontWeight: 600,
                letterSpacing: '1.5px', textTransform: 'uppercase',
                padding: '4px 10px', borderRadius: '999px',
                border: `1px solid ${filter === f ? d.accent : d.border}`,
                backgroundColor: filter === f ? d.bgSurface : 'transparent',
                color: filter === f ? d.accent : d.textTertiary, cursor: 'pointer',
              }}>{f === 'all' ? 'All Types' : f === 'dining' ? 'Dining' : 'Activities'}</button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '6px' }}>
            {(['all', 'plans'] as ShowFilter[]).map((s) => (
              <button key={s} onClick={() => setShowFilter(s)} style={{
                fontFamily: "'Raleway', sans-serif", fontSize: fs.metaLabel, fontWeight: 600,
                letterSpacing: '1.5px', textTransform: 'uppercase',
                padding: '4px 10px', borderRadius: '999px',
                border: `1px solid ${showFilter === s ? d.accent : d.border}`,
                backgroundColor: showFilter === s ? d.bgSurface : 'transparent',
                color: showFilter === s ? d.accent : d.textTertiary, cursor: 'pointer',
              }}>{s === 'all' ? 'Plans and Travel' : 'Plans Only'}</button>
            ))}
          </div>
        </div>
      )}

      {/* ═══ ITINERARY ═══ */}
      {showMap ? (
        <MapView items={filtered} lodging={trip.lodging} />
      ) : (
        <div
          onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}
          style={{
            padding: '0 26px 80px',
            transform: `translateX(${swipeOffset}px)`,
            transition: isAnimating ? 'none' : swipeOffset === 0 ? 'transform 0.2s ease-out' : 'none',
            willChange: 'transform',
          }}
        >
          {(() => {
            const totalEntries = Object.values(timeline).reduce((n, arr) => n + arr.length, 0) + unscheduledTimeline.length;
            if (totalEntries === 0) {
              return (
                <div style={{ padding: '64px 16px 40px', textAlign: 'center' }}>
                  <p style={{
                    fontFamily: displayFont, fontSize: fs.bodyLarge, fontWeight: 600,
                    fontStyle: 'italic', color: d.textSecondary,
                    margin: 0, lineHeight: 1.4,
                  }}>
                    Your trip is a blank canvas.<br />Start adding plans.
                  </p>
                </div>
              );
            }
            return daysToShow.map((day) => {
              const dayEntries = timeline[day] || [];
              if (dayEntries.length === 0) return null; // Skip empty days when there's content elsewhere
              const { dayName } = formatDayHeader(day);
              const dateLabel = new Date(day + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              return (
                <div key={day} style={{ marginBottom: '24px' }}>
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                    paddingBottom: '10px', borderBottom: `1px solid rgba(200,146,58,0.12)`,
                    marginBottom: '4px',
                  }}>
                    <span style={{ fontFamily: "'Raleway', sans-serif", fontSize: fs.dayHeader, fontWeight: 700, letterSpacing: '3px', textTransform: 'uppercase', color: d.textSecondary }}>{dayName}</span>
                    <span style={{ fontFamily: "'Raleway', sans-serif", fontSize: fs.dayHeader, fontWeight: 300, color: d.textTertiary }}>{dateLabel}</span>
                  </div>
                  {dayEntries.map((entry) => {
                    if (entry.kind === 'item') {
                      const shiftedDayLabel = entry.shifted && entry.data.day
                        ? new Date(entry.data.day + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short' })
                        : undefined;
                      return <ActivityCard key={entry.data.itemId} item={entry.data} onClick={() => setSelectedItem(entry.data)} shiftedDayLabel={shiftedDayLabel} />;
                    }
                    return <TravelLine key={entry.data.entryId} entry={entry.data} firstName={entry.firstName} />;
                  })}
                </div>
              );
            });
          })()}
          {unscheduledTimeline.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', paddingBottom: '10px', borderBottom: `1px solid rgba(200,146,58,0.12)`, marginBottom: '4px' }}>
                <span style={{ fontFamily: "'Raleway', sans-serif", fontSize: fs.dayHeader, fontWeight: 700, letterSpacing: '3px', textTransform: 'uppercase', color: d.textTertiary }}>Unscheduled</span>
              </div>
              {unscheduledTimeline.map((entry) =>
                entry.kind === 'item' ? (
                  <ActivityCard key={entry.data.itemId} item={entry.data} onClick={() => setSelectedItem(entry.data)} />
                ) : null
              )}
            </div>
          )}

          {/* ═══ DESIGN CLOSER ═══ */}
          <div style={{ padding: '32px 0' }}>
            <OrnamentalDivider style={{ margin: '0 0 12px' }} />
            <div style={{
              fontFamily: "'Raleway', sans-serif", fontSize: fs.wordmark, fontWeight: 800,
              letterSpacing: '9px', textTransform: 'uppercase',
              color: 'rgba(200,146,58,0.20)', textAlign: 'center', marginBottom: '4px',
            }}>KREWE</div>
            <div style={{
              fontFamily: displayFont, fontSize: fs.detail, fontWeight: 400,
              fontStyle: 'italic', color: 'rgba(200,146,58,0.25)', textAlign: 'center',
            }}>{meta.destination}</div>
          </div>
        </div>
      )}

      {/* FAB — visible to planner and claimed crew only */}
      {canEdit && (
        <DesignButton tier={1} onClick={() => setShowAddModal(true)} style={{
          position: 'fixed', bottom: '90px', left: '50%', transform: 'translateX(-50%)', zIndex: 40,
        }}>+ Add Plan</DesignButton>
      )}

      {(showAddModal || editItem) && <AddItemModal tripId={tripId} tripDays={tripDays} existingItem={editItem || undefined} currentUid={currentUid} onClose={() => { setShowAddModal(false); setEditItem(null); }} />}
      {showLodgingModal && <EditLodgingModal tripId={tripId} lodging={trip.lodging || undefined} startDate={meta.startDate} endDate={meta.endDate} onClose={() => setShowLodgingModal(false)} />}
    </>
  );
}
